<template>
  <div class="mt-1">
    <div v-if="!value" class="italic text-sm">Not signed by the applicant.</div>
    <div v-else>
      <img v-if="previewSrc" :src="previewSrc" class="w-[320px] border-2 border-gray-300 bg-white" alt="Signature" />
      <div>
        IP Address: <b>{{ value.userIp || '—' }}</b>
      </div>
      <div>
        Date: <b>{{ formattedDate }}</b>
      </div>
      <div v-if="value.browser">
        Browser: <b>{{ value.browser }}</b>
      </div>
      <div v-if="value.device">
        Device: <b>{{ value.device }}</b>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import { normalizeSignature } from '~/composables/customForms/customFormSummary';
import { useApplicationStore } from '~/composables/stores/ApplicationStore';

/**
 * FormKit custom input (registered as `signature` in formkit.config.ts through `createInput`): the admin
 * counterpart of sa-help's `components/formkit/SignatureInput.vue`, shown when a custom-form step is
 * edited from the application page. The node value is a `SignatureAnswer`, usually `{ blobId, userIp,
 * timestamp }` — the image itself lives in blob storage and is fetched back below for the preview.
 * Display-only: the signature belongs to the applicant, so the admin can see it but never change it.
 */
const props = defineProps<{
  /** FormKit input context, passed by `createInput` */
  context: any;
}>();

const value = computed(() => normalizeSignature(props.context._value));

const formattedDate = computed(() => {
  const timestamp = value.value?.timestamp ?? '';
  const date = DateTime.fromISO(timestamp);
  return date.isValid ? date.toFormat('M/d/yyyy hh:mm a (ZZZZ)') : timestamp;
});

const useApplication = useApplicationStore();
const applicationId = useRoute().params.id as string;
const resolvedImage = ref('');
watch(
  value,
  async (sig) => {
    resolvedImage.value = '';
    if (!sig?.blobId || sig.image) return;
    const res: any = await useApplication.getSasDocument(applicationId, sig.blobId, 'signature.png');
    resolvedImage.value = res?.data?.url ?? res?.url ?? '';
  },
  { immediate: true }
);
const previewSrc = computed(() => value.value?.image || resolvedImage.value);
</script>
